<script lang="ts" setup>
import { ref, reactive, onMounted } from "vue";
import { useRouter } from "vue-router";
import Pager from "@/helpers/Pager";
import { Resource } from "@/helpers/Interfaces";
import { Form } from "@/helpers/Form";
import SubstitutionService, {
    ListItemModel,
} from "@/modules/core/substitutions/services/SubstitutionService";
import {
    WidgetModel,
    WidgetConfigModel,
} from "@/modules/core/dashboards/services/DashboardsService";
import { useLocalization } from "@/plugins/localization";
import { useAlerts } from "@/plugins/alerts";
import WidgetComponentWrapper from "@/modules/core/dashboards/components/helpers/WidgetComponentWrapper.vue";
import FieldText from '@/modules/core/dashboards/components/properties/FieldText.vue';
import FieldNumeric from '@/modules/core/dashboards/components/properties/FieldNumeric.vue';
import FieldDimensions from '@/modules/core/dashboards/components/properties/FieldDimensions.vue';

const config = defineModel<WidgetConfigModel>('config');

defineProps<{
    widget: WidgetModel;
    propertiesMode: boolean;
}>();

const { $alert } = useAlerts();
const { $t } = useLocalization();
const router = useRouter();

const items = ref<Resource<ListItemModel>[]>([]);
const pager = ref<Pager>(null);

const filter = reactive(
    Form.create({
        userBeingReplaced: null,
        replacingUser: null,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        onlyActive: true,
    })
);

const canDelete = (item: Resource<ListItemModel>) =>
    item.meta.canDelete && item.result.canBeDeleted;

const onRowClick = (item: Resource<ListItemModel>) =>
{
    router.push({
        name: "core-substitutions-edit",
        params: { id: item.result.id },
    });
};

const onDeleteClick = async (item: Resource<ListItemModel>) =>
{
    try
    {
        await SubstitutionService.remove(item.result.id);
        $alert.success($t("[[[Zastępstwo zostało usunięte.]]]"));
        await loadData();
    }
    catch (ex: any)
    {
        if (ex.code == 400) $alert.warning(ex.message);
    }
};

const loadData = async () =>
{
    pager.value.loading = true;

    try
    {
        const response = await SubstitutionService.getList(
            filter.data(),
            pager.value as Pager
        );

        pager.value.setTotalRows(response.totalRows);
        items.value = response.items;
    }
    catch (ex)
    {
        pager.value.setTotalRows(0);
        items.value = [];
    }
    finally
    {
        pager.value.loading = false;
    }
};

const init = () =>
{
    const pageSize = config.value.sourceConfig.pageSize ?? 0;

    pager.value = new Pager(1, pageSize, "DateBeginUtc", "DESC");
    loadData();
};

onMounted(() =>
{
    init();
});
</script>

<template>
    <widget-component-wrapper :properties-mode="propertiesMode">
        <template #default>
            <list-view
                v-if="pager"
                :items="items"
                :pager="pager"
                :row-click="onRowClick"
                @change="loadData()"
            >
                <template #row="{ item }">
                    <list-view-header :title="$t('[[[Imię i Nazwisko]]]')">
                        {{ item.result.replacedByUserFirstName }}
                        {{ item.result.replacedByUserLastName }}
                    </list-view-header>
                    <list-view-item :title="$t('[[[Zastępuje osobę]]]')">
                        {{ item.result.userBeingReplacedFirstName }}
                        {{ item.result.userBeingReplacedLastName }}
                    </list-view-item>
                    <list-view-item
                        :title="$t('[[[Data rozpoczęcia]]]')"
                        sort="dateBeginUtc"
                        width="150"
                    >
                        {{ $filters.datetime(item.result.dateBeginUtc) }}
                    </list-view-item>
                    <list-view-item
                        :title="$t('[[[Data zakończenia]]]')"
                        sort="dateEndUtc"
                        width="150"
                    >
                        {{ $filters.datetime(item.result.dateEndUtc) }}
                    </list-view-item>
                    <list-view-buttons width="50">
                        <list-view-button
                            :title="$t('[[[Edytuj]]]')"
                            @click="onRowClick(item)"
                        >
                            <i class="icon far fa-pencil-alt"></i>
                            {{ $t("[[[Edytuj]]]") }}
                        </list-view-button>
                        <list-view-button
                            :title="$t('[[[Usuń]]]')"
                            :disabled="!canDelete(item)"
                            :message="$t('[[[Potwierdzenie usunięcia]]]')"
                            @confirm="onDeleteClick"
                        >
                            <i class="icon far fa-times"></i>
                            {{ $t("[[[Usuń]]]") }}
                        </list-view-button>
                    </list-view-buttons>
                </template>
            </list-view>

            <router-link :to="{ name: 'core-substitutions' }" class="link-more">
                {{ $t("[[[Zobacz wszystkie zastępstwa]]]") }}
            </router-link>
        </template>

        <template #properties>
            <field-text :model-value="widget.name" :label="$t('[[[Nazwa]]]')" disabled />
            <field-text :model-value="widget.description" :label="$t('[[[Opis]]]')" textarea disabled />
            <field-numeric v-model="config.sourceConfig.pageSize" :label="$t('[[[Liczba wyników na stronie]]]')" />
            <field-dimensions v-model:width="config.width" v-model:height="config.height" />
        </template>
    </widget-component-wrapper>
</template>

<style lang="scss" scoped>
.link-more {
    display: inline-block;
    margin-top: 15px;
    font-size: 1rem;
    line-height: 1.75rem;
    color: var(--bs-primary);
}
</style>
